/**
 * [C35] The identity card "إعدادات الورقة الرسمية" (`CompanyCard`, from App.tsx:15037-15377; D-125, D-148, D-149, O-4):
 * reads the office settings (`settings`), writes each changed `identity.*` key through `PUT /settings` with the caller's
 * own PIN (D-123, one PIN for the whole save; each key is audited with before / after, D-134). The logo is read here
 * as a data URI (PNG / JPEG / WebP, ≤ 300 KB) and saved at once, as the old card did.
 */
import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CompanyCard, type CompanyCardValues } from "@faylasuf/ui";
import type { PinProof } from "../../api/types.js";
import { useApp, useSession } from "../../app/context.js";
import { errorMessage, isSessionLost } from "../../app/errors.js";
import { usePinGate } from "../../app/pin-gate.js";
import { keys } from "../../app/query.js";
import { useToast } from "../../app/toast.js";

const FIELDS: ReadonlyArray<[keyof CompanyCardValues, string]> = [
  ["name", "identity.company_name"],
  ["phone1", "identity.phone1"],
  ["phone2", "identity.phone2"],
  ["address", "identity.address"],
  ["managerName", "identity.manager_name"],
  ["accountantName", "identity.accountant_name"],
  ["digitalCommissionRate", "identity.digital_commission_default_pct"],
  ["showProfitInPrints", "identity.show_profit_in_prints"],
  ["voucherFormats", "identity.voucher_formats"],
];
const LOGO_MAX = 300 * 1024;

export function CompanyContainer({ customIcon }: { customIcon?: string }) {
  const { source, t } = useApp();
  const { refresh, sessionLost } = useSession();
  const toast = useToast();
  const qc = useQueryClient();
  const gate = usePinGate();
  const settings = useQuery({ queryKey: keys.settings, queryFn: () => source.settings() });
  const fail = (e: unknown) => (isSessionLost(e) ? sessionLost() : toast(errorMessage(t, e), "error"));
  const val = (k: string): unknown => settings.data?.[k]?.value;
  const stored = (): CompanyCardValues => ({
    name: String(val("identity.company_name") ?? ""),
    phone1: String(val("identity.phone1") ?? ""),
    phone2: String(val("identity.phone2") ?? ""),
    address: String(val("identity.address") ?? ""),
    managerName: String(val("identity.manager_name") ?? ""),
    accountantName: String(val("identity.accountant_name") ?? ""),
    digitalCommissionRate: String(val("identity.digital_commission_default_pct") ?? "0.1"),
    showProfitInPrints: val("identity.show_profit_in_prints") !== false,
    voucherFormats: { customer_entry: "a4", transfer_voucher: "a4", installment_receipt: "a4", sales_invoice: "a4", ...((val("identity.voucher_formats") as object | undefined) ?? {}) },
  });
  const [edit, setEdit] = useState<CompanyCardValues | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => setEdit(null), [settings.dataUpdatedAt]);
  const values = edit ?? stored();
  const logo = typeof val("identity.logo") === "string" && (val("identity.logo") as string) ? (val("identity.logo") as string) : null;

  const done = async () => {
    await qc.invalidateQueries({ queryKey: keys.settings });
    await qc.invalidateQueries({ queryKey: ["office-logo"] });
    await refresh(); // session.identity / session.print feed every print header
  };
  const save = async () => {
    if (!edit) return;
    const base = stored();
    const changed = FIELDS.filter(([f]) => JSON.stringify(edit[f]) !== JSON.stringify(base[f]));
    if (changed.length === 0) return setEdit(null);
    setBusy(true);
    try {
      const r = await gate.withPin(
        async (proof: PinProof) => {
          for (const [f, key] of changed) await source.setSetting(key, typeof edit[f] === "string" ? (edit[f] as string).trim() : edit[f], proof.pin ?? "");
          return true;
        },
        { message: t("company.pin_message") },
      );
      if (r !== undefined) {
        toast(t("company.saved"));
        await done();
      }
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };
  const saveLogo = async (dataUrl: string) => {
    setBusy(true);
    try {
      const r = await gate.withPin((proof: PinProof) => source.setSetting("identity.logo", dataUrl, proof.pin ?? ""), { message: t("company.pin_logo") });
      if (r !== undefined) {
        toast(t("company.logo_saved"));
        await done();
      }
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };
  const onLogoFile = (file: File) => {
    if (!["image/png", "image/jpeg", "image/webp"].includes(file.type)) return toast(t("company.logo_type"), "error");
    if (file.size > LOGO_MAX) return toast(t("company.logo_too_large"), "error");
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === "string") void saveLogo(reader.result);
    };
    reader.readAsDataURL(file);
  };

  return (
    <CompanyCard
      values={values}
      logoUrl={logo}
      dirty={edit !== null}
      busy={busy}
      onChange={(patch) => setEdit({ ...values, ...patch })}
      onSave={() => void save()}
      onLogoFile={onLogoFile}
      onLogoRemove={() => void saveLogo("")}
      {...(customIcon ? { customIcon } : {})}
    />
  );
}
